import { Fragment, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  useClaims,
  useCreateJob,
  useExpenses,
  useJob,
  useJobsYear,
  useMarkJobClaimed,
  useSettingsPage,
} from "../api/hooks";
import type { ClaimJob, EntryData, HmrcCity, JobRow } from "../api/types";
import { errorText, useFlash } from "../components/Flash";
import { HmrcCityPicker } from "../components/HmrcCityPicker";
import { LoadError, Loading, useYear, Stale } from "../components/Layout";
import { CHECK_LABELS, CHECKS, entryAmounts, fxFor, MEAL_LABELS, MEALS, ratesFor } from "../lib/expenses";
import { dshort, dspan, money, num, todayISO, toNum } from "../lib/format";
import { PHONE, useMediaQuery } from "../lib/useMediaQuery";
import { cityFields, currencySymbol, HMRC_FIELDS, type HmrcRateKey } from "../lib/hmrc";

export function JobsPage() {
  const year = useYear();
  const q = useJobsYear(year);
  const [open, setOpen] = useState<Set<number>>(() => new Set());
  const expenses = useExpenses();
  const claims = useClaims(year, expenses);
  const phone = useMediaQuery(PHONE);
  if (q.isPending) return <Loading />;
  if (q.error) return <LoadError error={q.error} />;
  const { jobs, totals, role_summary, empty } = q.data;
  const columns = expenses ? 6 : 3;
  const claimOf = new Map((claims.data ?? []).map((c) => [c.job_id, c]));
  const toClaim = jobs.filter((j) => {
    const c = claimOf.get(j.id);
    return c && c.lines.length > 0 && c.status !== "claimed";
  }).length;
  const roleName = new Map(role_summary.map((r) => [r.code, r.name]));
  // The year's breakdown, under the table: only what was claimed.
  const breakdown = [
    totals.mileage > 0 && `${num(totals.mileage)} miles`,
    totals.travel > 0 && `Travel ${money(totals.travel)}`,
    totals.meals > 0 && `Meals ${money(totals.meals)}`,
    totals.hotel > 0 && `Hotel ${money(totals.hotel)}`,
    totals.extra > 0 && `Extra ${money(totals.extra)}`,
  ].filter(Boolean);
  const toggle = (id: number) =>
    setOpen((o) => {
      const n = new Set(o);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <Stale on={q.isPlaceholderData}>
      <div className="page-head">
        <h1>
          {expenses ? "Jobs and expenses" : "Jobs"}, {year}
        </h1>
        {expenses && (
          <a className="small" href={`/api/export/${year}.csv`}>
            Download {year} as CSV
          </a>
        )}
      </div>

      <AddJob year={year} openByDefault={jobs.length === 0} expenses={expenses} />

      <div className="panel" style={{ marginTop: "1.25rem" }}>
        {jobs.length ? (
          <div className="table-wrap">
            <table className="jobs-table">
              <thead>
                <tr>
                  {expenses && (
                    <th>
                      <span className="visually-hidden">Show the claim</span>
                    </th>
                  )}
                  <th>Job</th>
                  <th>Dates</th>
                  <th className="num">Days</th>
                  {expenses && <th className="num">Total</th>}
                  {expenses && <th className="claimed-cell">Claimed</th>}
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <Fragment key={j.id}>
                    <tr className={open.has(j.id) ? "is-unfolded" : undefined}>
                      {expenses && (
                        <td className="fold-cell">
                          <button
                            type="button"
                            className="fold-btn"
                            aria-expanded={open.has(j.id)}
                            aria-controls={`claim-${j.id}`}
                            aria-label={`${open.has(j.id) ? "Hide" : "Show"} the claim for ${j.name}`}
                            onClick={() => toggle(j.id)}
                          >
                            {open.has(j.id) ? "▾" : "▸"}
                          </button>
                        </td>
                      )}
                      <td>
                        <Link to={`/job/${j.id}`}>{j.name}</Link>
                      </td>
                      <td>
                        {phone ? (
                          dspan(j.first, j.last)
                        ) : (
                          <>
                            {dshort(j.first)}
                            {j.last !== j.first && ` to ${dshort(j.last)}`}
                          </>
                        )}
                      </td>
                      <td className="num">{j.days}</td>
                      {expenses && (
                        <td className="num">
                          <strong>{money(j.total)}</strong>
                        </td>
                      )}
                      {expenses && (
                        <td className="claimed-cell">
                          <ClaimStatus claim={claimOf.get(j.id)} year={year} />
                        </td>
                      )}
                    </tr>
                    {expenses && open.has(j.id) && (
                      <tr className="claim-row" id={`claim-${j.id}`}>
                        <td colSpan={columns}>
                          <JobClaim row={j} role={j.role ? roleName.get(j.role) : undefined} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  {expenses && <td />}
                  <td>{totals.jobs} jobs</td>
                  <td />
                  <td className="num">{num(totals.days)}</td>
                  {expenses && <td className="num">{money(totals.total)}</td>}
                  {expenses && (
                    <td className="claimed-cell">
                      {claims.data && (toClaim ? `${toClaim} to claim` : "All claimed")}
                    </td>
                  )}
                </tr>
              </tfoot>
            </table>
            {expenses && breakdown.length > 0 && (
              <p className="jobs-breakdown small muted">{breakdown.join(" · ")}</p>
            )}
          </div>
        ) : (
          <p>
            No jobs in {year} yet. Add your first job above, or{" "}
            <Link to="/import">import your spreadsheet</Link>.
          </p>
        )}
      </div>

      <div className="two-col" style={{ marginTop: "1.25rem" }}>
        <div className="panel">
          <h2>By role</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Role</th>
                  <th className="num">Jobs</th>
                  <th className="num">Days</th>
                </tr>
              </thead>
              <tbody>
                {role_summary.map((r) => (
                  <tr key={r.code || "none"}>
                    <td>{r.name}</td>
                    <td className="num">{r.jobs}</td>
                    <td className="num">{r.days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {empty.length > 0 && (
          <div className="panel">
            <h2>Jobs with no days</h2>
            <p className="small muted">These don't count anywhere until they have at least one day.</p>
            <ul>
              {empty.map((j) => (
                <li key={j.id}>
                  <Link to={`/job/${j.id}`}>{j.name}</Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Stale>
  );
}

// Every item a day can claim, in the job editor's order and groups. Items with `value` show it (miles, £,
// nights) instead of a tick; `sum` totals them in the footer, the rest count their ticks.
interface ClaimItem {
  label: string;
  count: (e: EntryData) => number;
  value?: (n: number) => string;
}
const CLAIM_GROUPS: { name: string; items: ClaimItem[] }[] = [
  {
    name: "Travel",
    items: [
      { label: "Miles", count: (e) => e.mileage, value: num },
      { label: "Train", count: (e) => e.train, value: money },
      { label: "Taxi", count: (e) => e.taxi, value: money },
      { label: "Parking", count: (e) => e.parking, value: money },
    ],
  },
  {
    name: "Meal allowance",
    items: MEALS.map((k) => ({ label: MEAL_LABELS[k].toLowerCase(), count: (e: EntryData) => e[k] })),
  },
  { name: "HMRC", items: CHECKS.map((k) => ({ label: CHECK_LABELS[k], count: (e: EntryData) => e[k] })) },
  {
    name: "",
    items: [
      { label: "Hotel nights", count: (e) => e.hotel_nights ?? 0, value: num },
      { label: "Extra", count: (e) => e.extra, value: money },
    ],
  },
];
// Flat, with each column's group (for the shading and dividers) and whether it starts one.
const CLAIM_COLUMNS = CLAIM_GROUPS.flatMap((g, gi) =>
  g.items.map((it, i) => ({ ...it, group: gi, first: i === 0 })),
);

/** What to claim for a job, unfolded under its row: the role and amounts, then a read-only grid of every
 * item, with a tick (or the amount) where it's claimed. Edit the job to change it. */
/** Whether the job's Concur claim is in (services/claims.py): claimed, partly, changed since, or not yet.
 * Click to mark the whole job as claimed by hand, right-click (or Delete) to set it back to to claim. */
function ClaimStatus({ claim, year }: { claim?: ClaimJob; year: number }) {
  const mark = useMarkJobClaimed(year);
  const flash = useFlash();
  if (!claim) return null;
  const lines = claim.lines.filter((l) => l.status !== "removed");
  if (lines.length === 0) return <span className="muted">Nothing to claim</span>;
  const done = lines.filter((l) => l.status === "claimed").length;
  const [label, cls, why] =
    claim.status === "claimed"
      ? ["Claimed", "", claim.claimed_before ? "Claimed before Work Tracker kept track (Settings)." : ""]
      : claim.status === "changed"
        ? ["Changed", "is-part", "Changed since it was added to Concur: update the claim there."]
        : claim.status === "partial"
          ? [`${done} of ${lines.length} lines`, "is-part", ""]
          : !claim.job_code
            ? [
                "Needs Concur job code",
                "is-todo",
                "Give the job its Concur job code to claim it with the extension.",
              ]
            : ["To claim", "is-todo", ""];
  const set = (claimed: boolean) => {
    if (!claimed && claim.claimed_before)
      return flash(
        `${claim.name} ended before your Concur claims tracking date, so it counts as claimed. Change the date in Settings to track it.`,
        "error",
      );
    if (claimed === (claim.status === "claimed")) return;
    mark.mutate(
      { jobId: claim.job_id, claimed },
      {
        onSuccess: () => flash(`${claim.name}: ${claimed ? "claimed" : "to claim"}.`),
        onError: (err) => flash(errorText(err), "error"),
      },
    );
  };
  return (
    <button
      type="button"
      className={`badge badge-btn ${cls}`}
      disabled={mark.isPending}
      title={`${why} Click: claimed. Right-click: to claim.`.trim()}
      aria-label={`${label}. Press to mark ${claim.name} as claimed, or Delete to mark it as to claim.`}
      onClick={() => set(true)}
      onContextMenu={(e) => {
        e.preventDefault();
        set(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Delete" || e.key === "Backspace") set(false);
      }}
    >
      {label}
    </button>
  );
}

function JobClaim({ row, role }: { row: JobRow; role?: string }) {
  const id = row.id;
  const q = useJob(id);
  // The cell under the pointer: its row and column are highlighted, so a mark is easy to place.
  const [at, setAt] = useState<{ r: number; c: number } | null>(null);
  const facts = [
    role && `Role: ${role}`,
    row.mileage > 0 && `${num(row.mileage)} miles`,
    row.travel > 0 && `Travel ${money(row.travel)}`,
    row.meals > 0 && `Meals ${money(row.meals)}`,
    row.hotel > 0 && `Hotel ${money(row.hotel)}`,
    row.extra > 0 && `Extra ${money(row.extra)}`,
  ].filter(Boolean);
  const head = facts.length > 0 && <p className="claim-facts">{facts.join(" · ")}</p>;
  if (q.isPending) return <p className="small muted">Loading…</p>;
  if (q.error) return <p className="small">Couldn't load this job's days. {errorText(q.error)}</p>;
  const { job, entries, rates, fx } = q.data;
  if (!entries.length) return <p className="small muted">No days on this job yet.</p>;
  const days = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  const totals = days.map(
    (e) =>
      entryAmounts(e, job, ratesFor(rates, e.date), job.hmrc_currency ? fxFor(fx, e.date)?.rate : undefined)
        .total,
  );
  const cls = (c: (typeof CLAIM_COLUMNS)[number], ci: number, r?: number) =>
    [
      "tick",
      c.value && "amount",
      c.group % 2 === 1 && "shade",
      c.first && "group-start",
      at?.c === ci && "col-on",
      r !== undefined && at?.r === r && "row-on",
    ]
      .filter(Boolean)
      .join(" ");
  const hover = (e: React.PointerEvent) => {
    const cell = (e.target as Element).closest<HTMLElement>("[data-c]");
    setAt(cell ? { r: Number(cell.dataset.r ?? -1), c: Number(cell.dataset.c) } : null);
  };

  return (
    <div className="claim">
      {head}
      <div className="table-wrap">
        <table className="claim-table" onPointerOver={hover} onPointerLeave={() => setAt(null)}>
          <thead>
            <tr className="claim-groups">
              <th />
              {CLAIM_GROUPS.map((g, gi) => (
                <th
                  key={gi}
                  colSpan={g.items.length}
                  className={`group-start${gi % 2 === 1 ? " shade" : ""}`}
                >
                  {g.name}
                </th>
              ))}
              <th />
            </tr>
            <tr>
              <th>Date</th>
              {CLAIM_COLUMNS.map((c, ci) => (
                <th key={c.label} className={cls(c, ci)} data-c={ci}>
                  {c.label}
                </th>
              ))}
              <th className="num group-start">Total</th>
            </tr>
          </thead>
          <tbody>
            {days.map((e, r) => (
              <tr key={e.date} className={at?.r === r ? "row-on" : undefined}>
                <td className="claim-date">{dshort(e.date)}</td>
                {CLAIM_COLUMNS.map((c, ci) => {
                  const n = c.count(e);
                  return (
                    <td key={c.label} className={cls(c, ci, r)} data-r={r} data-c={ci}>
                      {n === 0 ? (
                        <span className="none" aria-label="Not claimed">
                          ·
                        </span>
                      ) : c.value ? (
                        c.value(n)
                      ) : (
                        <span aria-label="Claimed">✓</span>
                      )}
                    </td>
                  );
                })}
                <td className="num group-start">{money(totals[r])}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="claim-date">
                {days.length} {days.length === 1 ? "day" : "days"}
              </td>
              {CLAIM_COLUMNS.map((c, ci) => {
                const n = c.value
                  ? days.reduce((a, e) => a + c.count(e), 0)
                  : days.filter((e) => c.count(e) !== 0).length;
                return (
                  <td key={c.label} className={cls(c, ci)} data-c={ci}>
                    {n === 0 ? "" : c.value ? c.value(n) : `${n}×`}
                  </td>
                );
              })}
              <td className="num group-start">{money(totals.reduce((a, b) => a + b, 0))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <Link className="claim-edit" to={`/job/${id}`}>
        Edit this job
      </Link>
    </div>
  );
}

function AddJob({
  year,
  openByDefault,
  expenses,
}: {
  year: number;
  openByDefault: boolean;
  expenses: boolean;
}) {
  const settings = useSettingsPage(year);
  const create = useCreateJob();
  const flash = useFlash();
  const navigate = useNavigate();
  const today = todayISO();
  const [form, setForm] = useState({
    name: "",
    role: "",
    start: today.startsWith(String(year)) ? today : `${year}-01-01`,
    end: "",
    hmrc_city: "",
    hmrc_currency: "",
    drink_rate: "0",
    lunch_rate: "0",
    dinner_rate: "0",
    hotel_rate: "0",
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const pickCity = (c: HmrcCity | null) => setForm((f) => ({ ...f, ...cityFields(c) }));
  const roles = settings.data?.roles ?? [];
  const role = form.role || roles[0]?.code || "";

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        name: form.name,
        role: role === "-" ? null : role || null,
        start: form.start,
        end: form.end || null,
        hmrc_city: form.hmrc_city || null,
        hmrc_currency: form.hmrc_currency || null,
        ...(Object.fromEntries(HMRC_FIELDS.map(([k]) => [k, toNum(form[k])])) as Record<HmrcRateKey, number>),
      },
      { onSuccess: (job) => navigate(`/job/${job.id}`), onError: (err) => flash(errorText(err), "error") },
    );
  };

  return (
    <details className="add panel" open={openByDefault}>
      <summary>
        <span className="btn">Add a job</span>
      </summary>
      <form onSubmit={submit}>
        <div className="fields">
          <div className="wide">
            <label htmlFor="name">Job name</label>
            <input id="name" required placeholder="e.g. UCL MD 3" value={form.name} onChange={set("name")} />
          </div>
          <div>
            <label htmlFor="role">Role</label>
            <select id="role" value={role} onChange={set("role")}>
              {roles.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name}
                </option>
              ))}
              <option value="-">No role</option>
            </select>
          </div>
          <div>
            <label htmlFor="start">First day</label>
            <input id="start" type="date" required value={form.start} onChange={set("start")} />
          </div>
          <div>
            <label htmlFor="end">Last day</label>
            <input id="end" type="date" value={form.end} onChange={set("end")} />
          </div>
        </div>
        {expenses && (
          <>
            <h3 style={{ marginTop: "1rem" }}>HMRC subsistence rates for this job</h3>
            <p className="small muted">
              Only needed for overseas jobs. Choose the city to fill in HMRC's rates; you can still change
              them.
            </p>
            <HmrcCityPicker id="hmrc_city" value={form.hmrc_city} onPick={pickCity} />
            <div className="fields">
              {HMRC_FIELDS.map(([k, , label]) => (
                <div key={k}>
                  <label htmlFor={k}>
                    {label} ({currencySymbol(form.hmrc_currency)})
                  </label>
                  <input id={k} type="number" step="0.01" min="0" value={form[k]} onChange={set(k)} />
                </div>
              ))}
            </div>
          </>
        )}
        <div className="actions">
          <button className="btn" type="submit" disabled={create.isPending}>
            Create job and add days
          </button>
        </div>
      </form>
    </details>
  );
}
